// src/features/services/sales/components/SalesOrderList.tsx
// ─────────────────────────────────────────────────────────────────────────────
// Compact list of sales orders with pipeline-stage badges.
// PAC Glass surfaces. Data owned by useSalesOrders hook.
// ─────────────────────────────────────────────────────────────────────────────
import React, { useState, useCallback } from 'react';
import { useSalesOrders, usePipelineStatus } from '../hooks/useSalesOrders';
import type { SalesOrder } from '../api/salesOrderApi';

const STAGE_COLORS: Record<string, string> = {
  pending:          'bg-amber-900/40 text-amber-400 border-amber-700/40',
  'device-linked':  'bg-blue-900/40 text-blue-400 border-blue-700/40',
  provisioned:      'bg-purple-900/40 text-purple-400 border-purple-700/40',
  active:           'bg-emerald-900/40 text-emerald-400 border-emerald-700/40',
  decommissioned:   'bg-neutral-800 text-neutral-500 border-neutral-700/40',
};

const STAGE_LABELS: Record<string, string> = {
  pending:          'awaiting device',
  'device-linked':  'device linked',
  provisioned:      'provisioned',
  active:           'active',
  decommissioned:   'decommissioned',
};

function StageBadge({ stage }: { stage: string }) {
  const cls = STAGE_COLORS[stage] ?? 'bg-neutral-800 text-neutral-400 border-neutral-700/40';
  return (
    <span className={`inline-block px-2 py-0.5 text-[10px] uppercase tracking-wider rounded border ${cls}`}>
      {STAGE_LABELS[stage] ?? stage}
    </span>
  );
}

// ── Detail panel for a selected order ────────────────────────────────────────

function OrderDetail({ serial }: { serial: string }) {
  const { data: pipeline, isLoading } = usePipelineStatus(serial);

  if (isLoading) return <p className="text-xs text-neutral-500 animate-pulse">Loading pipeline…</p>;
  if (!pipeline) return <p className="text-xs text-neutral-500">No pipeline data.</p>;

  return (
    <div className="pac-glass-raised rounded p-4 space-y-3 border border-neutral-800">
      {/* order summary */}
      <div className="flex items-center gap-3">
        <StageBadge stage={pipeline.pipeline_stage} />
        <span className="text-sm text-neutral-300">{pipeline.order.label}</span>
      </div>

      {/* next-step hint for pending orders */}
      {pipeline.pipeline_stage === 'pending' && (
        <p className="text-xs text-amber-400/70 italic">
          Next step → connect an ESP32 via USB and run PACDaemon provisioning
        </p>
      )}

      {/* line items */}
      {pipeline.line_items.length > 0 && (
        <div>
          <p className="text-[10px] text-neutral-500 uppercase tracking-wider mb-1">
            Line Items ({pipeline.line_items.length})
          </p>
          <div className="space-y-1">
            {pipeline.line_items.map((li) => (
              <div key={li.serial_number} className="flex items-center gap-3 text-xs">
                <span className="font-mono text-neutral-300 w-40 shrink-0">{li.serial_number}</span>
                <StageBadge stage={li.status} />
                {li.device_id && (
                  <span className="text-purple-400 font-mono text-[10px]">{li.device_id}</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* latest telemetry */}
      {pipeline.latest_telemetry && pipeline.latest_telemetry.length > 0 && (
        <div>
          <p className="text-[10px] text-neutral-500 uppercase tracking-wider mb-1">
            Latest Telemetry
          </p>
          <div className="grid grid-cols-3 gap-3">
            {pipeline.latest_telemetry.slice(0, 3).map((t, i) => (
              <div key={i} className="pac-glass-subtle rounded p-2 text-xs text-neutral-300">
                {t.temperature != null && <p>🌡 {String(t.temperature)}°C</p>}
                {t.humidity != null && <p>💧 {String(t.humidity)}%</p>}
                {t.rssi != null && <p>📶 {String(t.rssi)} dBm</p>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* device info */}
      {pipeline.device && (
        <div className="text-xs text-neutral-400">
          Device: <span className="text-purple-400 font-mono">{pipeline.device.device_id}</span>
        </div>
      )}
    </div>
  );
}

// ── Main list ────────────────────────────────────────────────────────────────

export default function SalesOrderList() {
  const { data, isLoading, isError, error } = useSalesOrders({ limit: 50 });
  const [selected, setSelected] = useState<string | null>(null);

  const toggle = useCallback(
    (s: string) => setSelected((prev) => (prev === s ? null : s)),
    [],
  );

  const orders: SalesOrder[] = data?.items ?? [];

  if (isLoading) {
    return (
      <div className="pac-glass rounded-lg p-5">
        <p className="text-sm text-neutral-500 animate-pulse">Loading orders…</p>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="pac-glass rounded-lg p-5">
        <p className="text-sm text-rose-400">{(error as Error)?.message ?? 'Failed to load orders'}</p>
      </div>
    );
  }

  if (orders.length === 0) {
    return (
      <div className="pac-glass rounded-lg p-5">
        <p className="text-sm text-neutral-500">No sales orders yet.</p>
      </div>
    );
  }

  return (
    <div className="pac-glass rounded-lg divide-y divide-white/5">
      {orders.map((o) => (
        <div key={o.serial_number}>
          {/* clickable row */}
          <button
            onClick={() => toggle(o.serial_number)}
            className="w-full text-left px-5 py-3 flex items-center gap-4 hover:bg-white/[0.03] transition-colors"
          >
            <span className="font-mono text-sm text-neutral-200 w-44 shrink-0">
              {o.serial_number}
            </span>
            <StageBadge stage={o.status} />
            <span className="text-sm text-neutral-400 truncate flex-1">
              {o.label}
            </span>
            <span className="text-xs text-neutral-600">
              {o.sku} · x{o.quantity}
            </span>
          </button>

          {/* expanded detail */}
          {selected === o.serial_number && (
            <div className="px-5 pb-4">
              <OrderDetail serial={o.serial_number} />
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
